import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { naborIzGotovogo } from './nomerNabora';
import { useAuth } from '../auth/useAuth';
import { usePermissions } from '../auth/usePermissions';
import { PhonePult } from './PhonePult';
import { OknoZvonka } from './OknoZvonka';
import { OknoZvonkaOgrada } from './OknoZvonkaOgrada';
import { OknoObrabotki } from './OknoObrabotki';
import { PhoneContext } from './phoneContext';
import { fetchWorkplaceCredentials } from './api';
import { nabratNomerSLinii, pozvonitIzKartochki as pozvonitNaServere, reportRegistration, vybrannayaLiniya } from './workplaceApi';
import { useSipPhone } from './useSipPhone';

/**
 * Телефон оператора живёт НАД страницами (жалоба владельца 13.08).
 *
 * ⭐ Было так: телефон подключался внутри страницы кабинета. Стоило уйти на
 * любую другую страницу — компонент размонтировался, регистрация обрывалась, и
 * «телефон подключён» гасло. При этом статус на сервере оставался «Готов»:
 * робот считает человека свободным и набирает клиента, которого некому взять.
 * Хуже тишины только тишина, за которую заплатили минутой связи.
 *
 * Поэтому соединение держится здесь, выше маршрутов: переход между экранами
 * его не касается. Подключение по-прежнему по кнопке — регистрация означает
 * «я на линии», и включать её за человека, который просто открыл админку,
 * нельзя.
 */

/** Телефон был подключён в этой вкладке до перезагрузки страницы (`s726`). */
function bylPodklyuchenVoVkladke(klyuch: string): boolean {
  try {
    return window.sessionStorage.getItem(klyuch) === '1';
  } catch {
    return false;
  }
}

export function PhoneProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { can, sectionHidden } = usePermissions();
  const mayRegister = user != null && can('tel.workplace') && !sectionHidden('tel-workplace');
  const [dialRevision, setDialRevision] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const phone = useSipPhone(audioRef);

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [activeNumber, setActiveNumber] = useState<string | null>(null);

  const connect = useCallback(async () => {
    setConnecting(true);
    setConnectError(null);

    /*
     * ⭐ «Прогрев» динамика (13.08: тишина на звонке от робота).
     *
     * Браузер разрешает воспроизведение звука только после действия человека.
     * Исходящий звонок начинается с нажатия — разрешение есть; входящий
     * приходит сам, и попытка включить звук отклоняется молча: разговор идёт,
     * оператор слышит тишину. Разрешение берётся здесь, на клике
     * «Подключить», — один раз на всю смену.
     */
    /*
     * ⭐ Прогрева звука здесь БОЛЬШЕ НЕТ (13.08).
     *
     * Он глушил элемент на время пробного воспроизведения и, если браузер
     * отвечал не сразу, оставлял его заглушённым — звук пропадал во всех
     * звонках, включая те, что работали раньше. Разрешение на звук даёт сам
     * клик по этой кнопке; включает динамик тот, кто получает поток.
     */
    try {
      await phone.connect(await fetchWorkplaceCredentials());
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setConnectError(message ?? 'Не получилось подключить телефон.');
    } finally {
      setConnecting(false);
    }
  }, [phone]);

  // Accepted s686 pult keeps the active call on the operator's current page.

  /*
   * ⛔ `s726` ТЕЛЕФОН ПЕРЕЖИВАЕТ ПЕРЕЗАГРУЗКУ СТРАНИЦЫ (находка `s724`: после F5 человек оставался
   * «Готов» без телефона, а во время «Обработки» подключить его было нечем).
   *
   * Подключает телефон по-прежнему человек — кнопкой. Но если в ЭТОЙ вкладке он уже был
   * подключён (отметка живёт в sessionStorage: переживает F5, не переживает новую вкладку и
   * новый день), после перезагрузки страница поднимает его сама. Чужую вкладку и утреннее
   * открытие панели это не касается: там отметки нет, телефон молчит, а сервер к тому времени
   * уже снял «Готов» с человека без телефона.
   */
  const klyuchVkladki = user != null ? `opsmind.tel.podklyuchen.${user.id}` : null;
  const avtoProbovali = useRef(false);
  useEffect(() => {
    if (klyuchVkladki === null || !phone.registered) return;
    try { window.sessionStorage.setItem(klyuchVkladki, '1'); } catch { /* закрытое хранилище — без автоподключения */ }
  }, [klyuchVkladki, phone.registered]);
  const connectRef = useRef(connect);
  useEffect(() => { connectRef.current = connect; }, [connect]);
  useEffect(() => {
    if (klyuchVkladki === null || !mayRegister || avtoProbovali.current) return;
    avtoProbovali.current = true;
    if (!bylPodklyuchenVoVkladke(klyuchVkladki)) return;
    // Следующим ходом, а не в теле эффекта: подключение меняет состояние экрана.
    window.setTimeout(() => { void connectRef.current(); }, 0);
  }, [klyuchVkladki, mayRegister]);

  /*
   * `s726`: телефон держит ДРУГОЕ окно этого браузера. Окно с телефоном раз в две секунды
   * отмечается в общем хранилище; остальные окна по свежей отметке знают, что телефон жив, —
   * и не зовут подключать его второй раз, и не врут «не подключён».
   */
  const klyuchOkna = user != null ? `opsmind.tel.okno.${user.id}` : null;
  const [vDrugomOkne, setVDrugomOkne] = useState(false);
  useEffect(() => {
    if (klyuchOkna === null) return;
    /*
     * Жалоба менеджера 07.10: «через 5 минут звонилка переходит в недоступен». Окно с телефоном
     * уходило в фон, браузер через пять минут начинал будить его таймеры раз в минуту, отметка
     * раз в две секунды старела — и рабочее окно писало «Недоступен» при живом телефоне.
     * Теперь окно с телефоном держит ЗАМОК браузера (он не зависит от таймеров и снимается сам,
     * когда окно закрыли), а остальные окна спрашивают, занят ли замок. Отметка в хранилище
     * остаётся запасным признаком для браузеров без замков.
     */
    const zamki = typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : undefined;
    const otmena = new AbortController();
    let otpustit: (() => void) | null = null;
    if (phone.registered && zamki) {
      void zamki.request(klyuchOkna, { signal: otmena.signal }, () => new Promise<void>((resolve) => { otpustit = resolve; }))
        .catch(() => undefined);
    }
    const otmetitsya = () => {
      try {
        if (phone.registered) {
          window.localStorage.setItem(klyuchOkna, String(Date.now()));
          return;
        }
        const svezhaya = Date.now() - Number(window.localStorage.getItem(klyuchOkna) ?? 0) < 6000;
        if (!zamki) {
          setVDrugomOkne(svezhaya);
          return;
        }
        void zamki.query()
          .then((sostoyanie) => setVDrugomOkne(svezhaya || (sostoyanie.held ?? []).some((z) => z.name === klyuchOkna)))
          .catch(() => setVDrugomOkne(svezhaya));
      } catch { /* хранилище закрыто — считаем, что другого окна нет */ }
    };
    otmetitsya();
    const timer = window.setInterval(otmetitsya, 2000);

    return () => {
      window.clearInterval(timer);
      otmena.abort();
      (otpustit as (() => void) | null)?.();
      // Уходя с телефоном, окно стирает отметку сразу — соседним не ждать шесть секунд.
      try { if (phone.registered) window.localStorage.removeItem(klyuchOkna); } catch { /* то же */ }
    };
  }, [klyuchOkna, phone.registered]);

  const pozvonitIzKartochki = useCallback(async (obyekt: { inquiry_id?: number; booking_id?: number }) => {
    try {
      // Линия, выбранная человеком в пульте, действует и для звонка из карточки.
      const liniya = vybrannayaLiniya();
      await pozvonitNaServere(liniya !== null ? { ...obyekt, line_id: liniya } : obyekt);
      setConnectError(null);

      return null;
    } catch (e: unknown) {
      return (e as { response?: { data?: { message?: string } } }).response?.data?.message
        ?? 'Звонок не начался: нет связи с телефонией.';
    }
  }, []);

  const nabratSLinii = useCallback(async (number: string, lineId: number) => {
    try {
      await nabratNomerSLinii(number, lineId);
      setConnectError(null);

      return null;
    } catch (e: unknown) {
      const slovo = (e as { response?: { data?: { message?: string } } }).response?.data?.message
        ?? 'Звонок не начался: нет связи с телефонией.';
      setConnectError(slovo);

      return slovo;
    }
  }, []);

  /*
   * Пока телефон зарегистрирован, кабинет отмечается на сервере: по этой
   * отметке решается, можно ли встать в «Готов». Раз в полминуты — отметка
   * живёт минуту, и закрытая вкладка перестаёт её продлевать сама.
   */
  useEffect(() => {
    if (!mayRegister) return;
    if (! phone.registered) {
      void reportRegistration(false).catch(() => {});

      return;
    }

    void reportRegistration(true).catch(() => {});

    const timer = window.setInterval(() => {
      void reportRegistration(true).catch(() => {});
    }, 30_000);

    return () => window.clearInterval(timer);
  }, [phone.registered, mayRegister]);

  const dial = useCallback(
    (number: string) => {
      /*
       * `s713` Д-13: единственная дверь звонка в панели — и ручной набор, и «Позвонить» из
       * карточек и журнала. Номер проверяется здесь тем же правилом, что в поле набора:
       * на линию уходит 7XXXXXXXXXX либо международный, а не «все цифры строки».
       */
      const nabor = naborIzGotovogo(number);
      if (nabor === null) {
        setConnectError(`Номер «${number}» не похож на номер — звонок не начат.`);

        return;
      }
      setConnectError(null);
      setDialRevision(revision => revision + 1);
      setActiveNumber(nabor);
      void phone.call(nabor);
    },
    [phone],
  );

  return (
    <PhoneContext.Provider
      value={{
        phone,
        connecting,
        connectError,
        connect,
        activeNumber,
        dialRevision,
        dial,
        vDrugomOkne: vDrugomOkne && !phone.registered,
        pozvonitIzKartochki: mayRegister ? pozvonitIzKartochki : undefined,
        nabratSLinii: mayRegister ? nabratSLinii : undefined,
      }}
    >
      {children}
      <PhonePult key={user?.id ?? 'anonymous'} />
      {/* `s766`: окно входящего звонка — рядом с телефоном, над маршрутами: всплывает на любой странице. */}
      <OknoZvonkaOgrada key={`okno-${user?.id ?? 'anonymous'}`}><OknoZvonka /></OknoZvonkaOgrada>
      {/* `s779`: срок обработки вышел, тишина в «Готов», «Отошёл» от станции — окна на любой странице. */}
      <OknoZvonkaOgrada key={`obrabotka-${user?.id ?? 'anonymous'}`}><OknoObrabotki /></OknoZvonkaOgrada>

      {/*
        Динамик разговора. Скрыт, но обязан быть в дереве и жить столько же,
        сколько соединение: без элемента браузеру некуда отдавать входящий
        звук, а элемент внутри страницы исчезал бы вместе с ней.
      */}
      <audio ref={audioRef} autoPlay className="hidden" />
    </PhoneContext.Provider>
  );
}
